import {useApolloClient} from "@apollo/client";
import {Modal, message} from "antd";
import moment from "moment";
import i18n from "../../../services/i18n";
import Roles from "../../../../shared/models/roles";
import useHighestRole from "../../../helpers/useHighestRole";
import {atLeast} from "../../common/AuthenticateSetup";
import {CurrencyShort, renderAmount} from "../../../helpers/renderAmount";
import {
    ConfirmBankTransactionDocument,
    ConfirmBankTransactionMutation,
    ConfirmBankTransactionMutationVariables,
    GetBankTransactionsForScheduledPaymentDocument,
    GetBankTransactionsForScheduledPaymentQuery,
    GetBankTransactionsForScheduledPaymentQueryVariables,
    useGetCompanyInfoQuery,
} from "../../../generated/graphql";

type Suggestion = GetBankTransactionsForScheduledPaymentQuery["bankTransactionsForScheduledPayment"][number];

const openOf = (rate: {paymentAmount: number; openAmount?: number | null}): number => Math.round(Number(rate.openAmount ?? rate.paymentAmount) * 100) / 100;

/**
 * Beim „Als bezahlt markieren“ einer Rate: Liegt im Bankabgleich ein offener Eingang, der genau diese Rate vorschlägt,
 * fragt der Dialog, ob der Bankumsatz übernommen werden soll (verbucht wie „Bestätigen“ im Board, mit Bankreferenz
 * als Beleg). Sonst oder bei „Ohne Bankabgleich“ läuft das bisherige Markieren.
 * Nur ab SuperAdmin mit freigeschaltetem Bankabgleich (gleiche Rollen wie „Bestätigen“ im Board).
 */
const useBankSuggestionForMarkPaid = () => {
    const client = useApolloClient();
    const highestRole = useHighestRole();
    const {data} = useGetCompanyInfoQuery({fetchPolicy: "cache-first"});
    const enabled = Boolean(data?.companyInfo?.subscription?.featureBankImport) && atLeast(Roles.SuperAdmin).includes(highestRole);

    /** Ruft `markManually` auf, wenn kein Vorschlag vorliegt oder der Nutzer ihn nicht übernimmt. */
    return async (scheduledPaymentId: string, markManually: () => void, onConfirmed: () => void): Promise<void> => {
        if (!enabled) {
            markManually();

            return;
        }
        let matches: Array<Suggestion> = [];

        try {
            const result = await client.query<GetBankTransactionsForScheduledPaymentQuery, GetBankTransactionsForScheduledPaymentQueryVariables>({
                query: GetBankTransactionsForScheduledPaymentDocument,
                variables: {scheduledPaymentId},
                fetchPolicy: "network-only",
            });

            // Nur Vorschläge, die heute noch exakt passen: Hat sich der offene Betrag einer Rate seit dem Import geändert
            // (Teilzahlung, Korrektur), würde die Abkürzung den Rest still als Guthaben buchen.
            matches = result.data.bankTransactionsForScheduledPayment.filter((candidate) => Math.abs(candidate.proposedScheduledPayments.reduce((sum, rate) => sum + openOf(rate), 0) - Number(candidate.amount)) < 0.005);
        } catch (error) {
            // Der Vorschlag ist eine Komfortfunktion; ohne ihn bleibt das Markieren möglich.
            matches = [];
        }
        const tr = i18n.containers.payments.BankImport.markPaidSuggestion;

        if (matches.length === 0) {
            markManually();

            return;
        }
        // Mehrere Umsätze passen (z. B. doppelte Überweisung in zwei Dateien): nicht still einen wählen.
        if (matches.length > 1) {
            Modal.confirm({
                title: tr.multipleTitle(),
                content: tr.multipleContent({count: String(matches.length)}),
                okText: tr.multipleOk(),
                cancelText: tr.manual(),
                closable: false,
                maskClosable: false,
                keyboard: false,
                onCancel: () => markManually(),
            });

            return;
        }
        const suggestion = matches[0];
        const rates = suggestion.proposedScheduledPayments;
        const amount = renderAmount({amount: Number(suggestion.amount), currency: suggestion.currency as CurrencyShort});
        const date = moment(suggestion.valueDate ?? suggestion.bookingDate).format("L");
        const id = suggestion.id;

        Modal.confirm({
            title: tr.title(),
            content: rates.length > 1 ? tr.contentFamily({date, amount, payer: suggestion.counterpartName ?? "", count: String(rates.length)}) : tr.content({date, amount, payer: suggestion.counterpartName ?? ""}),
            okText: tr.ok(),
            cancelText: tr.manual(),
            // Zwei-Wege-Wahl: AntD ruft onCancel auch bei X, Maske und Esc auf; ohne diese Wege ist „Abbrechen“ eindeutig
            // der Knopf „Ohne Bankabgleich markieren“. Der Nutzer wollte ohnehin als bezahlt markieren.
            closable: false,
            maskClosable: false,
            keyboard: false,
            onCancel: () => markManually(),
            onOk: async () => {
                try {
                    await client.mutate<ConfirmBankTransactionMutation, ConfirmBankTransactionMutationVariables>({
                        mutation: ConfirmBankTransactionDocument,
                        variables: {id, distributions: rates.map((rate) => ({scheduledPaymentId: rate.id, amount: openOf(rate)}))},
                        // Gleiche Listen wie beim Markieren von Hand (PaymentsListColumns.scheduledPaymentsRefetchQueries)
                        refetchQueries: ["GetBookingById", "GetBookingByIdWithCourseLessonBookings", "getUnpaidScheduledPayments", "getPaidScheduledPayments", "GetBankTransactions"],
                        awaitRefetchQueries: true,
                    });
                    message.success(tr.success());
                    onConfirmed();
                } catch (error) {
                    message.error((error as Error).message);
                }
            },
        });
    };
};

export default useBankSuggestionForMarkPaid;
